// src/prototype/hooks/useDemoUploads.ts
// ============================================================
// DEMO UPLOADS STORE — client-side, static-safe (no backend)
//
// Holds what the presenter actually uploads during the demo:
//   · documents  — files dropped on the Document Upload surface, by their REAL filename
//   · initiatives — rows parsed from the Existing Initiatives spreadsheet
//
// Same localStorage + CustomEvent pattern as useDemoCompletion / useDemoPlan, so every
// surface reading this store stays in sync without a backend.
//
// WHY A STORE AND NOT COMPONENT STATE: the document list, the Docs tracker on /home, and the
// Ctrl/Cmd+Shift+D reset all have to agree about what has been uploaded. Component state
// would desync the moment the presenter navigates away from the upload page and back.
//
// Both keys are in DEMO_STATE_KEYS, so the presenter reset clears them and the seed-version
// purge discards them when the version moves.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import type { EvidenceSource } from '../../lib/coverage/computeCoverage';

export const DEMO_DOCUMENTS_KEY = 'dva_demo_uploaded_documents';
export const DEMO_INITIATIVES_KEY = 'dva_demo_initiatives';
export const DEMO_UPLOADS_CHANGE_EVENT = 'dva_demo_uploads_change';

/** A file the presenter dropped. `name` is the real filename — never a placeholder. */
export interface DemoUploadedDocument {
  id: string;
  name: string;
  size: number;
  /** ISO string; set by the caller so this module stays deterministic. */
  uploadedAt: string;
  /**
   * The evidence this file produced, when it produced any: a Path A binding matched by
   * filename, or the scores a workbook's cells derived. Absent on a file that produced
   * none, which is the honest state for an unrecognised upload rather than an error.
   *
   * Stored on the record because the coverage panel has to survive a page change, and
   * re-reading the dropped File is not possible after one. Plain JSON by construction.
   * Adding this field is a persisted SHAPE change, which is why v7 bumps the seed version.
   */
  coverage?: EvidenceSource;
}

/** Status the dedupe story turns on. Derived, never authored in the template. */
export type InitiativeStatus = 'Matched' | 'Duplicate' | 'New';

export interface DemoInitiative {
  name: string;
  category: string;
  source: string;
  status: InitiativeStatus;
  /** How far the health system is into the initiative, 0–100. null = not supplied.
   *  Displayed only (Evaluate, Execute Summary); never a scoring input. */
  percentComplete?: number | null;
  /** The health system has already committed to this initiative, so it does not need to be
   *  evaluated. Default false. A Matched + committed row carries the flag onto its use case. */
  committed?: boolean;
  /** FTEs already committed (D5 proposal). Optional, display only. */
  ftesCommitted?: number | null;
  /** Library use case this row matched (Matched rows only). */
  matchedOpportunityId?: string | null;
  /** Row-level validation notes (rejected values, Committed/percent mismatch). */
  warnings?: string[];
}

interface UploadsState {
  documents: DemoUploadedDocument[];
  initiatives: DemoInitiative[] | null; // null = nothing uploaded yet
}

const EMPTY: UploadsState = { documents: [], initiatives: null };

function readState(): UploadsState {
  if (typeof window === 'undefined') return { ...EMPTY };
  try {
    const rawDocs = localStorage.getItem(DEMO_DOCUMENTS_KEY);
    const rawInit = localStorage.getItem(DEMO_INITIATIVES_KEY);
    const documents = rawDocs ? JSON.parse(rawDocs) : [];
    const initiatives = rawInit ? JSON.parse(rawInit) : null;
    return {
      documents: Array.isArray(documents) ? documents : [],
      initiatives: Array.isArray(initiatives) ? initiatives : null,
    };
  } catch {
    return { ...EMPTY };
  }
}

/**
 * The evidence uploaded documents carry, readable WITHOUT a hook.
 *
 * exportDataBuilder is a plain module called inside a useMemo, so it cannot call
 * useCoverage. It reads through here instead, which keeps ONE localStorage reader for this
 * key rather than a second copy of the parse-and-guard logic living in the export path.
 */
export function readEvidenceSources(): EvidenceSource[] {
  return readState()
    .documents.map((d) => d.coverage)
    .filter((c): c is EvidenceSource => Boolean(c));
}

function writeState(next: UploadsState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DEMO_DOCUMENTS_KEY, JSON.stringify(next.documents));
  if (next.initiatives === null) localStorage.removeItem(DEMO_INITIATIVES_KEY);
  else localStorage.setItem(DEMO_INITIATIVES_KEY, JSON.stringify(next.initiatives));
  window.dispatchEvent(new CustomEvent(DEMO_UPLOADS_CHANGE_EVENT, { detail: next }));
}

export interface DemoUploadsApi {
  documents: DemoUploadedDocument[];
  initiatives: DemoInitiative[] | null;
  /** Record an uploaded file. Returns the stored record. */
  addDocument: (
    file: { name: string; size?: number; coverage?: EvidenceSource },
    uploadedAt?: string,
  ) => DemoUploadedDocument;
  /** Every evidence source the uploaded documents carry, in upload order. */
  evidenceSources: EvidenceSource[];
  removeDocument: (id: string) => void;
  setInitiatives: (rows: DemoInitiative[]) => void;
  clearInitiatives: () => void;
  /** Back to the Act-1 baseline: nothing uploaded. */
  reset: () => void;
}

export function useDemoUploads(): DemoUploadsApi {
  const [state, setState] = useState<UploadsState>(() => readState());

  useEffect(() => {
    const handle = (e: Event) =>
      setState((e as CustomEvent<UploadsState>).detail ?? readState());
    window.addEventListener(DEMO_UPLOADS_CHANGE_EVENT, handle as EventListener);
    // The global presenter reset clears the keys and fires its own event.
    const onReset = () => setState(readState());
    window.addEventListener('ea_demo_reset', onReset);
    return () => {
      window.removeEventListener(DEMO_UPLOADS_CHANGE_EVENT, handle as EventListener);
      window.removeEventListener('ea_demo_reset', onReset);
    };
  }, []);

  const addDocument = useCallback(
    (file: { name: string; size?: number; coverage?: EvidenceSource }, uploadedAt?: string) => {
      const cur = readState();
      const rec: DemoUploadedDocument = {
        // Deterministic-ish id from the name + current length; no Math.random so repeated
        // demo runs behave identically.
        id: `demo-upload-${cur.documents.length + 1}-${file.name}`,
        name: file.name,
        size: file.size ?? 0,
        uploadedAt: uploadedAt ?? new Date().toISOString(),
        ...(file.coverage ? { coverage: file.coverage } : {}),
      };
      const next = { ...cur, documents: [...cur.documents, rec] };
      writeState(next);
      setState(next);
      return rec;
    },
    [],
  );

  const removeDocument = useCallback((id: string) => {
    const cur = readState();
    const next = { ...cur, documents: cur.documents.filter((d) => d.id !== id) };
    writeState(next);
    setState(next);
  }, []);

  const setInitiatives = useCallback((rows: DemoInitiative[]) => {
    const cur = readState();
    const next = { ...cur, initiatives: rows };
    writeState(next);
    setState(next);
  }, []);

  const clearInitiatives = useCallback(() => {
    const cur = readState();
    const next = { ...cur, initiatives: null };
    writeState(next);
    setState(next);
  }, []);

  const reset = useCallback(() => {
    writeState({ ...EMPTY });
    setState({ ...EMPTY });
  }, []);

  return {
    documents: state.documents,
    initiatives: state.initiatives,
    evidenceSources: state.documents
      .map((d) => d.coverage)
      .filter((c): c is EvidenceSource => Boolean(c)),
    addDocument,
    removeDocument,
    setInitiatives,
    clearInitiatives,
    reset,
  };
}

export default useDemoUploads;
